import dayjs from "dayjs";
import { deburr } from "lodash";

export type ExportSubject = {
    type: "organization" | "product" | "ingredient" | "packaging";
    id: number;
    name: string;
};

type ExportFilenameOptions = {
    subject?: ExportSubject;
    content: string;
    scope?: string;
    extension: string;
};

const SUBJECT_MAX_LENGTH = 50;
const CONTENT_MAX_LENGTH = 60;
const SCOPE_MAX_LENGTH = 30;
const EXCEL_SHEET_NAME_MAX_LENGTH = 31;
const DEFAULT_SHEET_NAME = "Export";

const toFilenameSegment = (value: string, maxLength: number): string => {
    const slug = deburr(value.normalize("NFKD"))
        .replace(/[^A-Za-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    if (slug.length <= maxLength) return slug;

    const boundary = slug.lastIndexOf("-", maxLength);
    return slug.slice(0, boundary > 0 ? boundary : maxLength);
};

export const buildExportFilename = (options: ExportFilenameOptions): string => {
    const { subject, content, scope, extension } = options;

    const subjectSegment =
        subject && (toFilenameSegment(subject.name, SUBJECT_MAX_LENGTH) || `${subject.type}-${subject.id}`);
    const segments = [
        subjectSegment,
        toFilenameSegment(content, CONTENT_MAX_LENGTH),
        toFilenameSegment(scope ?? "", SCOPE_MAX_LENGTH),
        dayjs().format("YYYY-MM-DD"),
    ].filter(Boolean);

    return `${segments.join("_")}.${extension}`;
};

export const buildExcelSheetName = (content: string): string => {
    const sheetName = content
        .replace(/[[\]:*?/\\]/g, "")
        .slice(0, EXCEL_SHEET_NAME_MAX_LENGTH)
        .replace(/[\uD800-\uDBFF]$/, "")
        .replace(/^[\s']+|[\s']+$/g, "");
    // SheetJS check_ws_name throws on a blank sheet name and on the reserved "History"
    if (sheetName === "" || sheetName.toLowerCase() === "history") return DEFAULT_SHEET_NAME;

    return sheetName;
};
